/**
 * FrontWindowShelfMapViewer – read-only shelf map entry point for Front Window workflows.
 *
 * Issue: #225 – CORE – Warehouse Shelf Map – Front Window Read-Only Integration
 *
 * Provides operational users with read-only access to the shelf map where they
 * already work. No edit/config controls are exposed. Context follows the active
 * warehouse/site from props.
 *
 * Features:
 *   - Read-only SVG map: select, zoom, and pan supported; no add/edit/delete.
 *   - Location details drawer opens on slot select (inventory view).
 *   - Active warehouse/site context drives the layout list.
 *   - Empty-state messaging when no published (active) map exists for the site.
 *   - Layout selector when multiple active layouts exist for the warehouse.
 */

import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Close, Map as MapIcon, Search } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import {
  useShelfMapLayouts,
  useShelfMapLayout,
  useShelfMapInventorySearch,
  useShelfMapContextLocations,
  useRootLocations,
} from '@/hooks/queries/shelf-map/useShelfMapLayouts';
import { shelfMapService } from '@/services/shelfMapService';
import { ShelfMapRenderer } from './ShelfMapRenderer';
import { ShelfMapDrawer } from './ShelfMapDrawer';
import { ShelfMapEligibilityGate } from './ShelfMapEligibilityGate';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FrontWindowShelfMapViewerProps {
  /**
   * The entity ID of the active warehouse/site root location.
   * When provided the viewer filters layouts to this warehouse only.
   * When null/undefined the viewer shows an "no warehouse context" empty state.
   */
  warehouseEntityId?: string | null;

  /**
   * Optional display name of the active warehouse, used in the header.
   */
  warehouseName?: string | null;

  handReceiptContext?: FrontWindowShelfMapHandReceiptContext | null;

  /**
   * Called when the user closes the viewer dialog.
   */
  onClose: () => void;
}

export interface FrontWindowShelfMapHandReceiptContext {
  customerName: string | null;
  thrItemCount: number;
  /** Distinct catalog LINs present on the loaded Temporary Hand Receipt, used to scope the map (#912). */
  lins: string[];
}

// ---------------------------------------------------------------------------
// Inner map content (layout selected)
// ---------------------------------------------------------------------------

interface ReadOnlyMapContentProps {
  layoutId: string;
  handReceiptContext?: FrontWindowShelfMapHandReceiptContext | null;
}

const ReadOnlyMapContent: React.FC<ReadOnlyMapContentProps> = ({
  layoutId,
  handReceiptContext,
}) => {
  const { data: layout, isLoading, isError } = useShelfMapLayout(layoutId);
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce search input by 350ms
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(searchInput), 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  const handleSelectSlot = (slotId: string, locationEntityId: string | null) => {
    setSelectedSlotId(slotId);
    setSelectedLocationId(locationEntityId);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedSlotId(null);
    setSelectedLocationId(null);
  };

  const selectedSlot = layout?.slots.find((s) => s.id === selectedSlotId);
  const selectedSlotLabel = selectedSlot?.location_name ?? null;
  const selectedSlotBreadcrumb = selectedSlot?.location_breadcrumb ?? null;
  const selectedSlotAisle = selectedSlot?.aisle_label ?? null;

  const allLayoutLocationIds = useMemo(() => {
    if (!layout) return [];
    return Array.from(
      new Set(layout.slots.map((slot) => slot.location_entity_id).filter(Boolean) as string[]),
    );
  }, [layout]);

  // Hand-receipt-scoped context (#912): when the viewer is launched from a
  // loaded Temporary Hand Receipt, resolve which locations in this layout
  // stock inventory matching the receipt's catalog LINs, and scope all
  // occupancy totals/highlighting to that set instead of the full site.
  const isContextScoped = (handReceiptContext?.lins.length ?? 0) > 0;
  const {
    locationIds: contextLocationIds,
    isLoading: contextLoading,
    isError: contextError,
    isPartial: contextPartial,
  } = useShelfMapContextLocations(layoutId, handReceiptContext?.lins ?? []);

  const mappedLocationIds = useMemo(() => {
    if (!isContextScoped) return allLayoutLocationIds;
    return allLayoutLocationIds.filter((id) => contextLocationIds.has(id));
  }, [allLayoutLocationIds, isContextScoped, contextLocationIds]);

  const inventoryPresenceQuery = useQuery({
    queryKey: ['shelf-map', 'inventory-presence-bulk', layoutId, mappedLocationIds],
    queryFn: () => shelfMapService.getLocationInventoryPresenceBulk(layoutId, mappedLocationIds),
    enabled: !!layout && mappedLocationIds.length > 0 && (!isContextScoped || !contextLoading),
    staleTime: 60_000,
    retry: 1,
    gcTime: 0,
  });

  const locationIdsWithInventory = useMemo(() => {
    const ids = new Set<string>();
    if (inventoryPresenceQuery.status !== 'success') return ids;

    for (const location of inventoryPresenceQuery.data.locations) {
      if (location.has_inventory) ids.add(location.location_id);
    }

    return ids;
  }, [inventoryPresenceQuery]);

  const resolvedLocationIds = useMemo(() => {
    if (inventoryPresenceQuery.status !== 'success') return new Set<string>();
    return new Set(mappedLocationIds);
  }, [inventoryPresenceQuery.status, mappedLocationIds]);

  const highlightedSlotIds = useMemo(() => {
    if (!layout) return new Set<string>();
    return new Set(
      layout.slots
        .filter(
          (slot) =>
            slot.location_entity_id != null &&
            locationIdsWithInventory.has(slot.location_entity_id),
        )
        .map((slot) => slot.id),
    );
  }, [layout, locationIdsWithInventory]);

  const noInventorySlotIds = useMemo(() => {
    if (!layout) return new Set<string>();
    return new Set(
      layout.slots
        .filter(
          (slot) =>
            slot.location_entity_id != null &&
            resolvedLocationIds.has(slot.location_entity_id) &&
            !locationIdsWithInventory.has(slot.location_entity_id),
        )
        .map((slot) => slot.id),
    );
  }, [layout, locationIdsWithInventory, resolvedLocationIds]);

  const inventoryPendingCount = inventoryPresenceQuery.isPending ? mappedLocationIds.length : 0;
  const inventoryErrorCount = inventoryPresenceQuery.isError ? mappedLocationIds.length : 0;

  // ── Inventory search (LIN / nomenclature) ─────────────────────────────
  const searchQuery = useShelfMapInventorySearch(layoutId, debouncedSearch);
  const searchMatchLocationIds = useMemo(() => {
    if (!searchQuery.data?.matches.length) return new Set<string>();
    return new Set(searchQuery.data.matches.map((m) => m.location_entity_id));
  }, [searchQuery.data]);

  // Slots whose location matches a search result → highlighted green
  const searchHighlightedSlotIds = useMemo(() => {
    if (!layout || searchMatchLocationIds.size === 0) return new Set<string>();
    return new Set(
      layout.slots
        .filter(
          (slot) =>
            slot.location_entity_id != null && searchMatchLocationIds.has(slot.location_entity_id),
        )
        .map((slot) => slot.id),
    );
  }, [layout, searchMatchLocationIds]);

  if (isLoading) {
    return <Skeleton variant="rounded" height="100%" />;
  }

  if (isError) {
    return <Alert severity="error">Failed to load map layout.</Alert>;
  }

  if (!layout) {
    return null;
  }

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'action.hover',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {/* Hand receipt context indicator (#912) — makes it explicit that
              totals below are scoped to the loaded Temporary Hand Receipt,
              not the full site. */}
          {isContextScoped && contextLoading && (
            <Chip
              label="Resolving hand receipt context…"
              size="small"
              color="info"
              variant="outlined"
              icon={<CircularProgress size={12} />}
            />
          )}
          {isContextScoped && !contextLoading && contextError && (
            <Chip
              label="Hand Receipt Context Unavailable"
              size="small"
              color="error"
              variant="filled"
            />
          )}
          {isContextScoped && !contextLoading && !contextError && contextPartial && (
            <Chip
              label="Hand Receipt Scoped (Incomplete)"
              size="small"
              color="warning"
              variant="filled"
            />
          )}
          {isContextScoped && !contextLoading && !contextError && !contextPartial && (
            <Chip label="Hand Receipt Scoped" size="small" color="primary" variant="filled" />
          )}
          {/* Inventory status chips */}
          <Chip label={`Occupied: ${highlightedSlotIds.size}`} size="small" color="default" />
          <Chip label={`Empty: ${noInventorySlotIds.size}`} size="small" color="error" />
          {inventoryPendingCount > 0 && (
            <Chip
              label={`Checking: ${inventoryPendingCount}`}
              size="small"
              color="info"
              variant="outlined"
            />
          )}
          {inventoryErrorCount > 0 && (
            <Chip
              label={`Failed checks: ${inventoryErrorCount}`}
              size="small"
              color="warning"
              variant="outlined"
            />
          )}
          {/* Search results chip */}
          {debouncedSearch.trim().length >= 2 && (
            <Chip
              label={
                searchQuery.isFetching ? 'Searching…' : `Matches: ${searchHighlightedSlotIds.size}`
              }
              size="small"
              color="success"
              variant={searchHighlightedSlotIds.size > 0 ? 'filled' : 'outlined'}
            />
          )}
          {/* Search field */}
          <Box sx={{ flex: 1, minWidth: 180, maxWidth: 280, ml: 'auto' }}>
            <TextField
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              size="small"
              placeholder="Search by LIN or nomenclature…"
              inputProps={{ 'aria-label': 'Search by LIN or nomenclature' }}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiInputBase-root': { fontSize: 12 } }}
            />
          </Box>
        </Stack>
      </Box>

      {isContextScoped && !contextLoading && contextError && (
        <Box sx={{ px: 2, pt: 2 }}>
          <Alert severity="warning">
            Unable to resolve this hand receipt&apos;s items against the shelf map. Location
            highlighting below may be incomplete — retry or verify inventory manually.
          </Alert>
        </Box>
      )}

      {isContextScoped && !contextLoading && !contextError && contextPartial && (
        <Box sx={{ px: 2, pt: 2 }}>
          <Alert severity="warning">
            Some of this hand receipt&apos;s items could not be resolved against the shelf map. The
            locations shown below may be incomplete.
          </Alert>
        </Box>
      )}

      {isContextScoped &&
        !contextLoading &&
        !contextError &&
        !contextPartial &&
        contextLocationIds.size === 0 && (
          <Box sx={{ px: 2, pt: 2 }}>
            <Alert severity="info">
              No locations in this warehouse currently stock items matching this hand receipt.
            </Alert>
          </Box>
        )}

      <Box sx={{ flex: 1, p: 2, overflow: 'hidden' }}>
        <ShelfMapRenderer
          slots={layout.slots}
          selectedSlotId={selectedSlotId}
          occupiedSlotIds={highlightedSlotIds}
          highlightedSlotIds={searchHighlightedSlotIds}
          noInventorySlotIds={noInventorySlotIds}
          onSelectSlot={handleSelectSlot}
        />
      </Box>

      {/* Details drawer — read-only inventory view */}
      <ShelfMapDrawer
        layoutId={layoutId}
        locationEntityId={selectedLocationId}
        slotLabel={selectedSlotLabel}
        slotBreadcrumb={selectedSlotBreadcrumb}
        aisleLabel={selectedSlotAisle}
        open={drawerOpen}
        onClose={handleCloseDrawer}
      />
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Main viewer dialog
// ---------------------------------------------------------------------------

/**
 * Full-screen read-only shelf map dialog for Front Window users.
 *
 * - No add/edit/delete controls are rendered.
 * - Layout selector appears when multiple active layouts exist for the warehouse.
 * - Empty state appears when no active layout exists.
 * - Location details drawer opens on slot click (inventory read-only).
 */
export const FrontWindowShelfMapViewer: React.FC<FrontWindowShelfMapViewerProps> = ({
  warehouseEntityId,
  warehouseName,
  handReceiptContext,
  onClose,
}) => {
  const [selectedLayoutId, setSelectedLayoutId] = useState<string | null>(null);
  const [pickedWarehouse, setPickedWarehouse] = useState<{ id: string; name: string } | null>(null);

  // When the caller has no warehouse context to pass (#1087), the empty state
  // below lets the user pick one instead of being stuck with no recovery path.
  const { data: rootLocations = [], isLoading: rootLocationsLoading } = useRootLocations();

  const effectiveWarehouseId = warehouseEntityId ?? pickedWarehouse?.id ?? null;
  const effectiveWarehouseName = warehouseEntityId
    ? warehouseName
    : (pickedWarehouse?.name ?? null);

  const { data: allLayouts = [], isLoading: layoutsLoading } = useShelfMapLayouts(
    effectiveWarehouseId ?? undefined,
  );

  // Only show active layouts to operational users
  const activeLayouts = allLayouts.filter((l) => l.is_active);

  // Auto-select the first layout when there is exactly one active layout
  const effectiveLayoutId =
    selectedLayoutId ?? (activeLayouts.length === 1 ? activeLayouts[0]!.id : null);

  const selectedLayout = activeLayouts.find((l) => l.id === effectiveLayoutId) ?? null;

  // ── Empty-state: no warehouse context ─────────────────────────────────────
  // Distinct from the "no published map" / "no active layout" states below —
  // those require an effectiveWarehouseId and must not be conflated with this
  // one (#1087).
  if (!effectiveWarehouseId) {
    return (
      <Dialog open fullScreen>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <ViewerHeader title="Shelf Map" subtitle={null} onClose={onClose} />
          <Box
            sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}
          >
            <Stack spacing={2} sx={{ width: '100%', maxWidth: 420 }}>
              <Alert severity="info">
                No warehouse context is active. Select an active warehouse or site to view its shelf
                map.
              </Alert>
              <FormControl fullWidth size="small" disabled={rootLocationsLoading}>
                <InputLabel id="fw-warehouse-picker-label">Warehouse / Site</InputLabel>
                <Select
                  labelId="fw-warehouse-picker-label"
                  label="Warehouse / Site"
                  value=""
                  onChange={(e) => {
                    const location = rootLocations.find((l) => l.id === e.target.value);
                    if (location) {
                      setPickedWarehouse({ id: location.id, name: location.display_name });
                    }
                  }}
                >
                  {rootLocations.map((location) => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.display_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Box>
        </Box>
      </Dialog>
    );
  }

  return (
    <Dialog open fullScreen>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* ── Header ──────────────────────────────────────────────────────── */}
        <ViewerHeader
          title={selectedLayout?.name ?? 'Shelf Map'}
          subtitle={
            handReceiptContext
              ? `${handReceiptContext.customerName ?? 'Customer'} (${handReceiptContext.thrItemCount} item${handReceiptContext.thrItemCount === 1 ? '' : 's'})`
              : (effectiveWarehouseName ?? selectedLayout?.warehouse_name ?? null)
          }
          onClose={onClose}
          readonly
        >
          {/* Layout selector – only rendered when there are multiple active layouts */}
          {activeLayouts.length > 1 && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="fw-layout-select-label">Map Layout</InputLabel>
              <Select
                labelId="fw-layout-select-label"
                value={effectiveLayoutId ?? ''}
                label="Map Layout"
                onChange={(e) => setSelectedLayoutId(e.target.value || null)}
              >
                {activeLayouts.map((l) => (
                  <MenuItem key={l.id} value={l.id}>
                    {l.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </ViewerHeader>

        {/* ── Content ─────────────────────────────────────────────────────── */}
        {layoutsLoading && (
          <Box sx={{ flex: 1, p: 2 }}>
            <Skeleton variant="rounded" height="100%" />
          </Box>
        )}

        {!layoutsLoading && activeLayouts.length === 0 && (
          <Box
            sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}
          >
            <Alert severity="info">
              No published shelf map is available for this warehouse. Contact your administrator to
              publish a map.
            </Alert>
          </Box>
        )}

        {!layoutsLoading && activeLayouts.length > 0 && !effectiveLayoutId && (
          <Box
            sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}
          >
            <Alert severity="info">Select a map layout above to view the shelf map.</Alert>
          </Box>
        )}

        {!layoutsLoading && effectiveLayoutId && (
          <ReadOnlyMapContent
            key={effectiveLayoutId}
            layoutId={effectiveLayoutId}
            handReceiptContext={handReceiptContext}
          />
        )}
      </Box>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Shared header
// ---------------------------------------------------------------------------

interface ViewerHeaderProps {
  title: string;
  subtitle: string | null;
  onClose: () => void;
  readonly?: boolean;
  children?: React.ReactNode;
}

const ViewerHeader: React.FC<ViewerHeaderProps> = ({
  title,
  subtitle,
  onClose,
  readonly = false,
  children,
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      px: 2,
      py: 1,
      borderBottom: '1px solid',
      borderColor: 'divider',
      gap: 2,
      flexWrap: 'wrap',
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center">
      <MapIcon color="primary" fontSize="small" />
      <Typography variant="h6">{title}</Typography>
      {subtitle && <Chip label={subtitle} size="small" variant="outlined" />}
      {readonly && <Chip label="Read Only" size="small" color="default" variant="filled" />}
    </Stack>

    <Stack direction="row" spacing={1} alignItems="center">
      {children}
      <Tooltip title="Close">
        <IconButton onClick={onClose} aria-label="Close shelf map viewer">
          <Close />
        </IconButton>
      </Tooltip>
    </Stack>
  </Box>
);

// ---------------------------------------------------------------------------
// Launcher button component (convenience wrapper for Front Window toolbar)
// ---------------------------------------------------------------------------

export interface FrontWindowShelfMapLauncherProps {
  warehouseEntityId?: string | null;
  warehouseName?: string | null;
  handReceiptContext?: FrontWindowShelfMapHandReceiptContext | null;
  /**
   * When true the button is force-disabled (e.g., role/eligibility gating
   * upstream). Does NOT cover "no warehouse resolved yet" — that case still
   * opens the viewer so the user can pick a warehouse from its empty state (#1087).
   */
  disabled?: boolean;
}

/**
 * Convenience launcher that renders a toolbar icon-button to open the
 * read-only shelf map viewer. Used inside Front Window panels.
 */
export const FrontWindowShelfMapLauncher: React.FC<FrontWindowShelfMapLauncherProps> = ({
  warehouseEntityId,
  warehouseName,
  handReceiptContext,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);

  if (disabled) {
    return (
      <Tooltip title="Shelf map unavailable">
        <Box component="span">
          <IconButton disabled aria-label="View shelf map" size="small">
            <MapIcon fontSize="small" />
          </IconButton>
        </Box>
      </Tooltip>
    );
  }

  // No warehouse context resolved yet (#1087): open the viewer directly
  // instead of disabling the button with no recovery path — the viewer's
  // empty state lets the user pick a warehouse/map without leaving Front
  // Window. The eligibility check needs a warehouse_entity_id, so it only
  // runs once one is known (below).
  if (!warehouseEntityId) {
    return (
      <>
        <Tooltip title="View Shelf Map">
          <Box component="span">
            <IconButton onClick={() => setOpen(true)} aria-label="View shelf map" size="small">
              <MapIcon fontSize="small" />
            </IconButton>
          </Box>
        </Tooltip>

        {open && (
          <FrontWindowShelfMapViewer
            warehouseEntityId={null}
            warehouseName={warehouseName}
            handReceiptContext={handReceiptContext}
            onClose={() => setOpen(false)}
          />
        )}
      </>
    );
  }

  return (
    <>
      <ShelfMapEligibilityGate
        eligibilityInput={{ warehouse_entity_id: warehouseEntityId, warehouse_only: true }}
        onEligible={() => setOpen(true)}
        renderTrigger={({ onClick, isChecking }) => (
          <Tooltip title="View Shelf Map">
            <Box component="span">
              <IconButton
                onClick={onClick}
                disabled={isChecking}
                aria-label="View shelf map"
                size="small"
              >
                {isChecking ? <CircularProgress size={20} /> : <MapIcon fontSize="small" />}
              </IconButton>
            </Box>
          </Tooltip>
        )}
      />

      {open && (
        <FrontWindowShelfMapViewer
          warehouseEntityId={warehouseEntityId}
          warehouseName={warehouseName}
          handReceiptContext={handReceiptContext}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
};
